import * as React from 'react';
import './styles.scss';
import cx from 'classnames';
import Input from 'components/input';
import notify, { Color } from 'utils/notify';

export interface PageAuthenticationProps {
  sso: ConfigOptions;
  google: ConfigOptions;
  teamOnly: TeamOnly;
  ipWhitelist: IpWhitelist;
}

interface ConfigOptions {
  path: string;
  enabled: boolean;
}

interface TeamOnly {
  show: boolean;
  enabled: boolean;
  path: string;
}

interface IpWhitelist {
  show: boolean;
  enabled: boolean;
  ips: string;
  path: string;
}

const PageAuthentication = ({
  sso,
  google,
  teamOnly,
  ipWhitelist,
}: PageAuthenticationProps) => {
  /** STATE VARIABLES */
  const [teamOnlyEnabled, setTeamOnlyEnabled] = React.useState(
    teamOnly.enabled,
  );
  const [ips, setIps] = React.useState(ipWhitelist.ips);
  const [ipWhitelistingEnabled, setIpWhitelistingEnabled] = React.useState(
    ipWhitelist.enabled,
  );

  /** UTILITY FUNCTIONS */
  const isSsoDisabled = () => {
    return (
      (google.enabled || teamOnlyEnabled || ipWhitelistingEnabled) &&
      !sso.enabled
    );
  };

  const isGoogleDisabled = () => {
    return (
      (sso.enabled || teamOnlyEnabled || ipWhitelistingEnabled) &&
      !google.enabled
    );
  };

  const isTeamOnlyDisabled = () => {
    return (
      (sso.enabled || google.enabled || ipWhitelistingEnabled) &&
      !teamOnlyEnabled
    );
  };

  const isIpWhitelistingDisabled = () => {
    return (
      (sso.enabled || google.enabled || teamOnlyEnabled) &&
      !ipWhitelistingEnabled
    );
  };

  const disableMessage = () => {
    let enabledOption = '';
    if (sso.enabled) enabledOption = 'SAML';
    else if (google.enabled) enabledOption = 'Google Authentication';
    else if (teamOnlyEnabled) enabledOption = 'team only access';
    else if (ipWhitelistingEnabled) enabledOption = 'IP whitelisting';

    return enabledOption ? `Disable ${enabledOption} to enable` : '';
  };

  const saveIpWhitelist = async () => {
    try {
      await $.ajax({
        url: ipWhitelist.path,
        method: 'PATCH',
        dataType: 'json',
        data: {
          page: {
            ip_filters_list: ips,
          },
        },
      });

      // if any IPs were present in the string, it's now enabled
      setIpWhitelistingEnabled(ips.length > 0);
      notify('IP whitelisting updated!', {
        color: Color.SUCCESS,
      });
    } catch (e) {
      notify('IP whitelisting failed to update, please try again.', {
        color: Color.ERROR,
      });
    }
  };

  const disableIpWhitelist = async () => {
    try {
      await $.ajax({
        url: ipWhitelist.path,
        method: 'PATCH',
        dataType: 'json',
        data: {
          page: {
            ip_filters_list: '',
          },
        },
      });

      setIpWhitelistingEnabled(false);
      setIps('');
      notify('IP whitelisting disabled.', {
        color: Color.SUCCESS,
      });
    } catch (e) {
      notify('IP whitelisting failed to be disabled, please try again.', {
        color: Color.ERROR,
      });
    }
  };

  const updateTeamOnly = async (isChecked: boolean) => {
    try {
      await $.ajax({
        url: teamOnly.path,
        method: 'PATCH',
        dataType: 'json',
        data: {
          page: {
            viewers_must_be_team_members: isChecked,
          },
        },
      });

      setTeamOnlyEnabled(isChecked);
      notify(`Team only access successfully set to ${isChecked.toString()}.`, {
        color: Color.SUCCESS,
      });
    } catch (e) {
      notify('Team only access settings failed to save, please try again.', {
        color: Color.ERROR,
      });
    }
  };

  /** RENDER FUNCTIONS */
  const renderSsoButton = () => {
    return renderConfigureButton(sso, isSsoDisabled(), 'configure-sso-btn');
  };

  const renderGoogleButton = () => {
    return renderConfigureButton(
      google,
      isGoogleDisabled(),
      'configure-google-btn',
    );
  };

  const renderConfigureButton = (
    options: ConfigOptions,
    disabled: boolean,
    id: string,
  ) => {
    const text = options.enabled ? 'Update' : 'Configure';
    const disabledClass = disabled ? 'disabled' : '';
    const classes = cx(
      'cpt-button style-outline color-grey style-secondary size-small',
      disabledClass,
    );

    return (
      <a href={options.path} className={classes} id={id}>
        {text}
      </a>
    );
  };

  const maybeRenderLegacyOptions = () => {
    return (
      <div className="legacy-options">
        {maybeRenderIpWhitelisting()}
        {maybeRenderTeamOnly()}
      </div>
    );
  };

  const maybeRenderIpWhitelisting = () => {
    if (ipWhitelist.show) {
      return (
        <div className="legacy-option-container ip-whitelisting">
          <div className="heading">
            <div className="title">IP whitelisting</div>
          </div>
          <Input
            type="text"
            label="Restrict IPs"
            placeholder="10.20.0.0/16,10.20.30.40"
            value={ips}
            onChange={e => setIps(e.target.value)}
            inputClass="full-width"
            id="ip-filters"
            helpBlockText="Comma-separated list of IP addresses allowed to access your page. Masks allowed."
          />
          <div className="footer two-items">
            <div className="disable-message">
              {isIpWhitelistingDisabled() ? disableMessage() : ''}
            </div>
            <div className="form-actions-container">
              {maybeRenderIpDisableButton()}
              <button
                onClick={saveIpWhitelist}
                className={`cpt-button style-primary ${
                  isIpWhitelistingDisabled() ? 'disabled' : null
                }`}
              >
                {ipWhitelistingEnabled ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      );
    }
  };

  const maybeRenderIpDisableButton = () => {
    if (ipWhitelistingEnabled) {
      return (
        <a onClick={disableIpWhitelist} className="btn-delete-config">
          Disable
        </a>
      );
    }
  };

  const maybeRenderTeamOnly = () => {
    if (teamOnly.show) {
      return (
        <div className="legacy-option-container team-only">
          <div className="heading">
            <div className="title">Team only access</div>
          </div>
          <label className="checkbox">
            <input
              type="checkbox"
              onChange={e => updateTeamOnly(e.target.checked)}
              defaultChecked={teamOnlyEnabled}
              disabled={isTeamOnlyDisabled()}
              id="team-only-access"
            />
            Only let me and my team members view our page
          </label>
          <div className="subtext">
            This will keep your page fully private and won’t let anybody except
            account holders on your team view the page.
          </div>
          <div className="footer">
            <div className="disable-message">
              {isTeamOnlyDisabled() ? disableMessage() : ''}
            </div>
          </div>
        </div>
      );
    }
  };

  /** FINAL RESULT */
  return (
    <div className="page-authentication">
      <h3>Authentication</h3>

      {maybeRenderLegacyOptions()}

      <div className="heading">
        <div className="title">Single sign-on</div>
        <div className="description">
          <strong>Private pages</strong> are viewable by authenticated users.
          Set up your SAML provider (OKTA, PingIdentity, Bitum, OneLogin) or
          Google Auth to require page viewers to log in to see the status page.
        </div>
      </div>

      <div className="config-options">
        <div className="config-row">
          <div className="description">
            <div className="main">SAML</div>
            {isSsoDisabled() ? (
              <div className="subtext">{disableMessage()}</div>
            ) : null}
          </div>
          <div className="actions">{renderSsoButton()}</div>
        </div>
        <div className="config-row">
          <div className="description">
            <div className="main">Google Auth</div>
            {isGoogleDisabled() ? (
              <div className="subtext">{disableMessage()}</div>
            ) : null}
          </div>
          <div className="actions">{renderGoogleButton()}</div>
        </div>
      </div>
    </div>
  );
};

export default PageAuthentication;
